import {
  AvatarSizeVariant,
  Button,
  Combobox,
  Modal,
  ModalProps,
  SelectOption,
  UserAvatar,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, type JSX } from 'react';

import useAddTeamMember from '@/api/mutations/teams/useAddTeamMember';
import useRemoveTeamMember from '@/api/mutations/teams/useRemoveTeamMember';
import { AccountUser } from './useUsersTableFilters';

type ManageableTeam = { id: string; name: string };

type CandidateMetadata = { user: AccountUser };

type Props = {
  team: ManageableTeam;
  /** All active account users, each carrying their team memberships. */
  users: AccountUser[];
} & Pick<ModalProps, 'isOpen' | 'onClose'>;

const isMemberOf = (user: AccountUser, teamId: string) =>
  user.teams.items.some((team) => team.id === teamId);

const ManageMembersBody: FC<Omit<Props, 'isOpen'>> = ({
  team,
  users,
  onClose,
}): JSX.Element => {
  const addMember = useAddTeamMember();
  const removeMember = useRemoveTeamMember();

  const members = users.filter((user) => isMemberOf(user, team.id));
  const candidates = users.filter((user) => !isMemberOf(user, team.id));

  const candidateOptions: SelectOption<string, CandidateMetadata>[] =
    candidates.map((user) => ({
      label: user.fullName || user.email || '',
      value: user.publicId,
      metadata: { user },
    }));

  return (
    <>
      <Modal.Body className="flex flex-col gap-4">
        <Combobox<string, false, CandidateMetadata>
          multiple={false}
          value={undefined}
          options={candidateOptions}
          hideDropdownChevron
          inputPlaceholder="Add a person by name"
          customButtonContent={
            <span className="flex items-center gap-2 text-gray-500">
              <UntitledIcon name="search-lg.3" className="h-4 w-4" />
              Add a person by name
            </span>
          }
          CustomOption={CandidateOption}
          onChange={(userId) => {
            if (userId) {
              addMember.mutate({ teamId: team.id, userId });
            }
          }}
        />

        <div className="mt-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Current members · {members.length}{' '}
            {members.length === 1 ? 'member' : 'members'}
          </span>
          {members.length === 0 ? (
            <p className="mt-2 text-gray-500">This team has no members yet.</p>
          ) : (
            <ul className="m-0 mt-2 flex list-none flex-col overflow-y-auto h-64 p-0">
              {members.map((member) => {
                const isRemoving =
                  removeMember.isPending &&
                  removeMember.variables?.userId === member.publicId;
                const otherTeams = member.teams.items.filter(
                  (t) => t.id !== team.id
                );

                return (
                  <li
                    key={member.id}
                    className="flex items-center gap-3 border-b border-gray-200 py-3 last:border-b-0"
                  >
                    <UserAvatar
                      sizeVariant={AvatarSizeVariant.SM}
                      user={{
                        firstName: member.firstName,
                        lastName: member.lastName,
                        emailAddress: member.email,
                        userColour: member.colour,
                        imgSrc: member.avatarUrl,
                      }}
                    />
                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="truncate font-medium">
                        {member.fullName}
                      </span>
                      <span className="truncate text-sm text-gray-500">
                        {member.systemRole}
                        {otherTeams.length > 0 &&
                          ` · also in ${otherTeams
                            .map((t) => t.name)
                            .join(', ')}`}
                      </span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isRemoving}
                      onClick={() =>
                        removeMember.mutate({
                          teamId: team.id,
                          userId: member.publicId,
                        })
                      }
                    >
                      <UntitledIcon name="minus.3" className="h-3.5 w-3.5" />
                      Remove
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button onClick={onClose}>Done</Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </>
  );
};

const CandidateOption: FC<{
  option: SelectOption<string, CandidateMetadata>;
}> = ({ option }): JSX.Element => {
  const { user } = option.metadata ?? {};

  return (
    <div className="flex items-center gap-3">
      {user && (
        <UserAvatar
          sizeVariant={AvatarSizeVariant.SM}
          user={{
            firstName: user.firstName,
            lastName: user.lastName,
            emailAddress: user.email,
            userColour: user.colour,
            imgSrc: user.avatarUrl,
          }}
        />
      )}
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate font-medium">{option.label}</span>
        {user && (
          <span className="truncate text-sm text-gray-500">
            {user.systemRole}
          </span>
        )}
      </div>
    </div>
  );
};

const ManageMembersModal: FC<Props> = ({
  team,
  users,
  isOpen,
  onClose,
}): JSX.Element => {
  return (
    <Modal isOpen={isOpen} title={team.name} onClose={onClose} size="sm">
      {isOpen && (
        <ManageMembersBody team={team} users={users} onClose={onClose} />
      )}
    </Modal>
  );
};

export default ManageMembersModal;
